import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface VersionInfo {
  version: string;
  buildTime: string;
}

interface UseAppUpdateOptions {
  /** Polling interval in milliseconds. Default: 5 minutes */
  pollingInterval?: number;
  /** Whether to enable polling. Default: true in production */
  enabled?: boolean;
}

interface UseAppUpdateReturn {
  /** Whether a new version is available */
  updateAvailable: boolean;
  /** Current version (from initial load) */
  currentVersion: string | null;
  /** New version (if update available) */
  newVersion: string | null;
  /** Manually check for updates */
  checkForUpdate: () => Promise<boolean>;
}

const VERSION_URL = '/version.json';
const DEFAULT_POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to detect when a new version of the app is available.
 * Silently reloads on route navigation or tab re-focus.
 */
export function useAppUpdate(options: UseAppUpdateOptions = {}): UseAppUpdateReturn {
  const {
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    enabled = import.meta.env.PROD,
  } = options;

  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialVersionRef = useRef<string | null>(null);
  const pendingUpdateRef = useRef(false);
  const location = useLocation();

  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch {
      return null;
    }
  }, []);

  const checkForUpdate = useCallback(async (): Promise<boolean> => {
    const versionInfo = await fetchVersion();

    if (!versionInfo) {
      return false;
    }

    // First load - store the initial version
    if (!initialVersionRef.current) {
      initialVersionRef.current = versionInfo.version;
      setCurrentVersion(versionInfo.version);
      return false;
    }

    // Compare versions
    if (versionInfo.version !== initialVersionRef.current) {
      setNewVersion(versionInfo.version);
      setUpdateAvailable(true);
      pendingUpdateRef.current = true;
      return true;
    }

    return false;
  }, [fetchVersion]);

  // Initial version fetch
  useEffect(() => {
    if (enabled) {
      checkForUpdate();
    }
  }, [enabled, checkForUpdate]);

  // Polling for updates
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const intervalId = setInterval(checkForUpdate, pollingInterval);

    return () => clearInterval(intervalId);
  }, [enabled, pollingInterval, checkForUpdate]);

  // Silent reload on tab re-focus when update is pending
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && pendingUpdateRef.current) {
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled]);

  // Silent reload on route navigation when update is pending
  useEffect(() => {
    if (pendingUpdateRef.current) {
      window.location.reload();
    }
  }, [location.pathname]);

  return {
    updateAvailable,
    currentVersion,
    newVersion,
    checkForUpdate,
  };
}
